import { Link } from "react-router";
import { cn } from "@tarawa/ui";
import { useI18n } from "@/lib/i18n";
import type { Pack } from "@/lib/types";
import { ProductImage } from "./product";

/** Pack tile on the store: image, name and fixed price. Opens the pack page (contents + add to cart). */
export function PackCard({ pack, className }: { pack: Pack; className?: string }) {
  const { ln, money, t } = useI18n();
  const name = ln(pack.name, pack.nameAr);
  return (
    <Link
      to={`/packs/${pack.id}`}
      className={cn(
        "group flex flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface shadow-[var(--shadow-card)] transition-shadow hover:shadow-lg",
        className,
      )}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <ProductImage
          src={pack.imageUrl}
          alt={name}
          seed={pack.id}
          className={cn("size-full transition-transform duration-300 group-hover:scale-[1.03]", !pack.isAvailable && "opacity-50 grayscale")}
        />
        <span className="absolute start-2 top-2 rounded-full bg-saffron-400 px-2.5 py-1 text-[11px] font-bold text-ink">{t("pack.badge")}</span>
        {!pack.isAvailable && (
          <span className="absolute end-2 top-2 rounded-full bg-ink/80 px-2.5 py-1 text-[11px] font-semibold text-white">{t("common.unavailable")}</span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <span className="line-clamp-2 text-sm leading-5 font-semibold text-ink">{name}</span>
        <span className="text-xs text-muted">{t("pack.items", { count: pack.items.length })}</span>
        <span className="ltr-nums mt-auto text-base font-extrabold text-brand-800">{money(pack.priceCents)}</span>
      </div>
    </Link>
  );
}
